<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

// Witcher is the default layout for nearly every route, so import it eagerly
// (in the main bundle) to avoid a flash of a different layout on slow networks
// while its separate chunk loads. Rarely-used layouts stay lazy.
import LayoutWitcher from '@core/layouts/layout-witcher/LayoutWitcher.vue'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () =>
  import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    LayoutWitcher
  },
  data() {
    return {}
  },
  created() {
    // App initialization
    console.log(
      'App created - current route:',
      this.$route.name,
      this.$route.path
    )
  },
  mounted() {
    // App mounted - router is ready
    console.log('App mounted - router ready')

    // Defer non-critical CSS variable reading to avoid blocking initial render
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.initThemeColors())
    } else {
      setTimeout(() => this.initThemeColors(), 0)
    }
  },
  methods: {
    // Initialize theme colors and breakpoints (deferred for performance)
    initThemeColors() {
      const colors = [
        'primary',
        'secondary',
        'success',
        'info',
        'warning',
        'danger',
        'light',
        'dark'
      ]

      colors.forEach(color => {
        $themeColors[color] = useCssVar(
          `--${color}`,
          document.documentElement
        ).value.trim()
      })

      const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

      breakpoints.forEach(bp => {
        $themeBreakpoints[bp] = Number(
          useCssVar(`--breakpoint-${bp}`, document.documentElement).value.slice(
            0,
            -2
          )
        )
      })
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      if (this.$route.meta.layout === 'witcher') return 'layout-witcher'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    }
  },
  beforeCreate() {
    // Set RTL only - CSS variables deferred to mounted for better performance
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade'
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses
    }
  }
}
</script>
